import type { AdminMetricsDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import { tokens } from '../../../design';
import { gaugeOffset, percent } from '../_lib/chartGeometry';

/**
 * Tile 3 — Online сейчас. Dark-accent tile (mirrors `.t-online` in
 * `design/mockups/admin/dashboard.html`) showing `online / total` active
 * inspectors and a % gauge (SVG ring) of the online share.
 *
 * NO charting dependency — the gauge is a plain SVG circle with a
 * computed `stroke-dashoffset` (see `chartGeometry.gaugeOffset`).
 */

const GAUGE_R = 34;
const GAUGE_C = 2 * Math.PI * GAUGE_R;

export interface OnlineNowTileProps {
  data: AdminMetricsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn: string;
  gridRow?: string;
}

export const OnlineNowTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
  gridRow,
}: OnlineNowTileProps): ReactElement => {
  const { t } = useTranslation();
  const online = data?.onlineInspectors;
  const onlineCount = online?.online ?? 0;
  const total = online?.total ?? 0;
  const pct = percent(onlineCount, total);
  const offset = gaugeOffset(onlineCount, total, GAUGE_C);

  return (
    <AnalyticsTile
      label={t('dashboard.online.title')}
      gridColumn={gridColumn}
      gridRow={gridRow}
      isLoading={isLoading}
      isError={isError}
      hasData={Boolean(online)}
      dark
    >
      {online ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
            <Box
              component="svg"
              viewBox="0 0 80 80"
              role="img"
              aria-label={t('dashboard.online.gaugeLabel', { pct })}
              sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}
            >
              <circle cx="40" cy="40" r={GAUGE_R} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="7" />
              <circle
                cx="40"
                cy="40"
                r={GAUGE_R}
                fill="none"
                stroke={tokens.state.success}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={GAUGE_C}
                strokeDashoffset={offset}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: tokens.font.mono,
                fontSize: 15,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {pct}%
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontFamily: tokens.font.mono, fontSize: 26, fontWeight: 700, lineHeight: 1, color: '#fff' }}>
              {onlineCount} / {total}
            </Box>
            <Box sx={{ mt: 0.5, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
              {t('dashboard.online.caption')}
            </Box>
          </Box>
        </Box>
      ) : null}
    </AnalyticsTile>
  );
};
